import { useMemo } from 'react';

/**
 * useClientAccess
 * 
 * Determines which clients a user has access to based on their role and team structure.
 * 
 * Business Logic:
 * - Admins: See all clients
 * - Account Managers: See their assigned_clients
 * - Team Leads: See their own assigned_clients
 * - Regular Recruiters: Inherit team lead's assigned_clients
 * 
 * @param {Object} selectedUser - Current user object
 * @param {Object} teamLead - User's team lead (for regular recruiters)
 * @param {Array} jobs - All jobs (to extract unique clients)
 * @returns {Object} - { assignedClients, allClients, accessType }
 */
export const useClientAccess = (selectedUser, teamLead, jobs) => {
  // Get all unique clients from jobs
  const allClientsFromJobs = useMemo(() => {
    const clients = new Set();
    jobs.forEach(job => {
      if (job.client_company) clients.add(job.client_company);
    });
    return Array.from(clients).sort();
  }, [jobs]);

  // Determine user's role
  const userRole = selectedUser?.role || selectedUser?.roles?.[0];
  const isAccountManager = userRole === 'account_manager' || 
                          selectedUser?.roles?.includes('account_manager');
  const isLeadRecruiter = userRole === 'lead_recruiter' || 
                         selectedUser?.roles?.includes('lead_recruiter') ||
                         userRole === 'recruiter_supervisor' ||
                         selectedUser?.roles?.includes('recruiter_supervisor');

  // Calculate client access based on role
  const clientAccess = useMemo(() => {
    // Admin roles - see all clients
    if (userRole === 'admin' || userRole === 'junoadmin') {
      return {
        assignedClients: allClientsFromJobs,
        allClients: allClientsFromJobs,
        accessType: 'admin'
      };
    }
    
    // Account Managers - use their assigned_clients
    if (isAccountManager) {
      const myClients = selectedUser.assigned_clients || [];
      return {
        assignedClients: myClients,
        allClients: allClientsFromJobs,
        accessType: 'account_manager'
      };
    }
    
    // Team Leads - use their personal assigned clients (not team's)
    if (isLeadRecruiter) {
      return {
        assignedClients: selectedUser.assigned_clients || [],
        allClients: allClientsFromJobs,
        accessType: 'team_lead'
      };
    }
    
    // Regular Recruiters - use team LEAD's assigned clients (inherit from team lead)
    if (userRole === 'recruiter') {
      const teamLeadClients = teamLead?.assigned_clients || [];
      return {
        assignedClients: teamLeadClients,
        allClients: allClientsFromJobs,
        accessType: 'team_member'
      };
    }
    
    // Default - no client access
    return {
      assignedClients: [],
      allClients: allClientsFromJobs,
      accessType: 'none'
    };
  }, [selectedUser, teamLead, isAccountManager, isLeadRecruiter, allClientsFromJobs, userRole]);

  return {
    ...clientAccess,
    isAccountManager,
    isLeadRecruiter
  };
};

export default useClientAccess;
